import { LOGO_MAX_SIZE, LOGO_ALLOWED_TYPES } from './constants';

// Email validation
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Phone validation (basic international format)
export const validatePhone = (phone) => {
  const phoneRegex = /^\+?[1-9]\d{1,14}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

// URL validation
export const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// Logo file validation
export const validateLogo = (file) => {
  const errors = [];

  if (!file) {
    return { isValid: true, errors: [] };
  }

  // Check file size
  if (file.size > LOGO_MAX_SIZE) {
    errors.push(`File size must not exceed ${LOGO_MAX_SIZE / 1024 / 1024}MB`);
  }

  // Check file type
  if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
    errors.push('File must be JPEG, PNG, or SVG format');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Time format validation (HH:mm)
export const validateTimeFormat = (time) => {
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  return timeRegex.test(time);
};

// Working hours validation
export const validateWorkingHours = (day, openTime, closeTime) => {
  if (!openTime || !closeTime) {
    return { isValid: false, error: 'Both open and close times are required' };
  }

  if (!validateTimeFormat(openTime) || !validateTimeFormat(closeTime)) {
    return { isValid: false, error: 'Invalid time format (use HH:mm)' };
  }

  const [openHour, openMin] = openTime.split(':').map(Number);
  const [closeHour, closeMin] = closeTime.split(':').map(Number);

  const openMinutes = openHour * 60 + openMin;
  const closeMinutes = closeHour * 60 + closeMin;

  if (closeMinutes <= openMinutes) {
    return { isValid: false, error: 'Close time must be after open time' };
  }

  return { isValid: true, error: null };
};

// Partner form validation rules
export const partnerFormRules = {
  name: [
    { required: true, message: 'Partner name is required' },
    { min: 3, message: 'Name must be at least 3 characters' },
    { max: 100, message: 'Name must not exceed 100 characters' },
  ],
  email: [
    { required: true, message: 'Email is required' },
    { type: 'email', message: 'Please enter a valid email' },
  ],
  phone: [
    { required: true, message: 'Phone number is required' },
    {
      validator: (_, value) => {
        if (!value || validatePhone(value)) {
          return Promise.resolve();
        }
        return Promise.reject(new Error('Please enter a valid phone number'));
      },
    },
  ],
  status: [{ required: true, message: 'Status is required' }],
};

// Clinic form validation rules
export const clinicFormRules = {
  clinic_name_ro: [
    { required: true, message: 'Clinic name (RO) is required' },
    { min: 3, message: 'Name must be at least 3 characters' },
  ],
  clinic_name_ru: [
    { required: true, message: 'Clinic name (RU) is required' },
    { min: 3, message: 'Name must be at least 3 characters' },
  ],
  clinic_name_en: [
    { required: true, message: 'Clinic name (EN) is required' },
    { min: 3, message: 'Name must be at least 3 characters' },
  ],
  address_ro: [
    { required: true, message: 'Address (RO) is required' },
    { min: 5, message: 'Address must be at least 5 characters' },
  ],
  address_ru: [
    { required: true, message: 'Address (RU) is required' },
    { min: 5, message: 'Address must be at least 5 characters' },
  ],
  address_en: [
    { required: true, message: 'Address (EN) is required' },
    { min: 5, message: 'Address must be at least 5 characters' },
  ],
};

// MAIB credentials validation rules
export const maibFormRules = {
  maib_merchant_id: [
    { required: false },
    { pattern: /^[A-Z0-9-]+$/, message: 'Invalid merchant ID format' },
  ],
  maib_terminal_id: [
    { required: false },
    { pattern: /^[A-Z0-9-]+$/, message: 'Invalid terminal ID format' },
  ],
  maib_signature_key: [
    { required: false },
    { min: 32, message: 'Signature key must be at least 32 characters' },
  ],
};